import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { volumesOptions } from "@/constants/options/volumes.option";
import TrashIcon from "@/assets/icons/trash.svg?react";
import ShipmentTransitIcon from "@/assets/icons/shipment-transit.svg?react";
import ShieldIcon from "@/assets/icons/shield.svg?react";
import { StyledSelectSingleValueInt } from "@/components/styled/StyledSelectSingleValueInt";
import StyledSwitch from "@/components/ui/customs/StyledSwitch";
import { CustomCotationFormData } from "@/types/forms/home/customCotationsForm.form";
import { StyledSelectSingleValue } from "@/components/styled/StyledSelectSingleValue";
import { StyledTextArea } from "@/components/styled/StyledTextArea";
import { ControlledInputV2 } from "@/components/ui/customs/controlled-input-v2";
import { useParcelProductTypeOptions } from "@/hooks/useEnumOptions";

export const InfoProductsParcel = () => {
  const { t } = useTranslation("boQuotations");
  const parcelProductTypeOptions = useParcelProductTypeOptions();
  const methods = useFormContext<CustomCotationFormData>();

  const productsFieldArray = useFieldArray({
    control: methods.control,
    name: "parcelProducts",
  });

  const packagesFieldArray = useFieldArray({
    control: methods.control,
    name: "parcelPackages",
  });

  const productWatch = useWatch({
    control: methods.control,
    name: "parcelProducts",
  });

  return (
    <div className="flex w-full flex-1 flex-col items-center gap-6">
      <div className="shadow-basic flex w-full max-w-[800px] flex-col items-center justify-center rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex flex-col items-center justify-center gap-4">
          <h2 className="text-brand700 text-xl font-semibold">
            {t("productsParcel.productsTitle")}
          </h2>
          <div className="flex w-full flex-col items-end gap-2">
            {productsFieldArray.fields.map((field, index) => (
              <div className="flex w-full flex-col items-start gap-2" key={field.id}>
                <p className="font-semibold text-gray-700">
                  {t("productsParcel.productTitle", { number: index + 1 })}
                </p>
                <div className="flex w-full flex-col items-start gap-2">
                  <StyledSelectSingleValue
                    options={parcelProductTypeOptions}
                    setValue={(e: any) => {
                      methods.setValue(`parcelProducts.${index}.type`, e);
                      methods.trigger([`parcelProducts.${index}.type`]);
                    }}
                    control={methods.control}
                    name={`parcelProducts.${index}.type`}
                    className="w-full"
                    title={t("productsParcel.productTypeLabel")}
                  />
                  {productWatch?.[index]?.type === "BOTTLE" ? (
                    <div className="flex w-full flex-col items-start gap-2 md:flex-row">
                      <div className="flex w-full flex-1 flex-col items-start sm:flex-row sm:gap-2">
                        <StyledSelectSingleValueInt
                          options={volumesOptions}
                          setValue={(e: any) => {
                            methods.setValue(`parcelProducts.${index}.volume`, e);
                            methods.trigger([`parcelProducts.${index}.volume`]);
                          }}
                          control={methods.control}
                          name={`parcelProducts.${index}.volume`}
                          className="w-full"
                          title={t("productsParcel.volumeLabel")}
                        />
                        <div className="flex w-full flex-row items-start gap-2">
                          <ControlledInputV2
                            label={t("productsParcel.bottleQuantityLabel")}
                            control={methods.control}
                            name={`parcelProducts.${index}.bottleQuantity`}
                            className="w-full"
                            type="number"
                            disableDecimal
                          />
                          {productsFieldArray.fields.length > 1 && (
                            <button
                              type="button"
                              onClick={() => productsFieldArray.remove(index)}
                              className="mt-[15px] flex items-start justify-center hover:bg-gray-100 focus:outline-hidden"
                            >
                              <TrashIcon className="cursor-pointer text-red-600" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex w-full flex-col items-start gap-2 md:flex-row">
                      <div className="flex w-full flex-1 flex-row items-start gap-2">
                        <StyledTextArea
                          title={t("productsParcel.descriptionLabel")}
                          control={methods.control}
                          name={`parcelProducts.${index}.description`}
                          className="w-full"
                          register={methods.register}
                        />
                        {productsFieldArray.fields.length > 1 && (
                          <button
                            type="button"
                            onClick={() => productsFieldArray.remove(index)}
                            className="mt-[15px] flex items-start justify-center hover:bg-gray-100 focus:outline-hidden"
                          >
                            <TrashIcon className="cursor-pointer text-red-600" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                productsFieldArray.append({
                  type: "BOTTLE",
                  volume: 0,
                  bottleQuantity: 0,
                  description: "",
                })
              }
              className="bg-brand50 text-brand700 hover:border-brand600 h-fit w-fit rounded-lg px-4 py-3 text-base font-semibold focus:outline-hidden"
            >
              {t("productsParcel.addProduct")}
            </button>
          </div>
          <h2 className="text-brand700 text-xl font-semibold">
            {t("productsParcel.parcelsTitle")}
          </h2>
          <div className="flex w-full flex-col items-end gap-2">
            {packagesFieldArray.fields.map((field, index) => (
              <div className="flex flex-col items-start gap-2" key={field.id}>
                <p className="font-semibold text-gray-700">
                  {t("productsParcel.parcelTitle", { number: index + 1 })}
                </p>
                <div className="hidden flex-col items-start gap-2 md:flex">
                  <div className="flex flex-row items-start gap-2">
                    <ControlledInputV2
                      label={t("productsParcel.lengthLabel")}
                      control={methods.control}
                      name={`parcelPackages.${index}.length`}
                      type="number"
                      className="w-full"
                      rightElement={<p className="text-xs text-gray-600">cm</p>}
                      disableDecimal
                    />
                    <ControlledInputV2
                      label={t("productsParcel.widthLabel")}
                      control={methods.control}
                      name={`parcelPackages.${index}.width`}
                      type="number"
                      className="w-full"
                      rightElement={<p className="text-xs text-gray-600">cm</p>}
                      disableDecimal
                    />
                    <ControlledInputV2
                      label={t("productsParcel.heightLabel")}
                      control={methods.control}
                      name={`parcelPackages.${index}.height`}
                      type="number"
                      className="w-full"
                      rightElement={<p className="text-xs text-gray-600">cm</p>}
                      disableDecimal
                    />
                  </div>
                  <div className="flex w-full flex-row items-start gap-2">
                    <ControlledInputV2
                      label={t("productsParcel.weightLabel")}
                      control={methods.control}
                      name={`parcelPackages.${index}.weight`}
                      type="number"
                      className="w-full"
                      rightElement={<p className="text-xs text-gray-600">kg</p>}
                      disableDecimal
                    />
                    <ControlledInputV2
                      label={t("productsParcel.valueLabel")}
                      control={methods.control}
                      name={`parcelPackages.${index}.value`}
                      className="w-full"
                      rightElement={<p>€</p>}
                      tooltip={t("productsParcel.valueTooltip")}
                    />
                    {packagesFieldArray.fields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => packagesFieldArray.remove(index)}
                        className="mt-[15px] flex items-start justify-center hover:bg-gray-100 focus:outline-hidden"
                      >
                        <TrashIcon className="cursor-pointer text-red-600" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-start gap-2 md:hidden md:flex-row">
                  <div className="flex flex-row items-start gap-2">
                    <ControlledInputV2
                      label={t("productsParcel.lengthLabel")}
                      control={methods.control}
                      name={`parcelPackages.${index}.length`}
                      type="number"
                      className="w-full"
                      rightElement={<p className="text-xs text-gray-600">cm</p>}
                      disableDecimal
                    />
                    <ControlledInputV2
                      label={t("productsParcel.widthLabel")}
                      control={methods.control}
                      name={`parcelPackages.${index}.width`}
                      type="number"
                      className="w-full"
                      rightElement={<p className="text-xs text-gray-600">cm</p>}
                      disableDecimal
                    />
                  </div>
                  <div className="flex flex-row items-start gap-2">
                    <ControlledInputV2
                      label={t("productsParcel.heightLabel")}
                      control={methods.control}
                      name={`parcelPackages.${index}.height`}
                      type="number"
                      className="w-full"
                      rightElement={<p className="text-xs text-gray-600">cm</p>}
                      disableDecimal
                    />
                    <ControlledInputV2
                      label={t("productsParcel.weightLabel")}
                      control={methods.control}
                      name={`parcelPackages.${index}.weight`}
                      type="number"
                      className="w-full"
                      rightElement={<p className="text-xs text-gray-600">kg</p>}
                      disableDecimal
                    />
                  </div>
                  <div className="flex w-full flex-row items-start gap-2">
                    <ControlledInputV2
                      label={t("productsParcel.valueLabel")}
                      control={methods.control}
                      name={`parcelPackages.${index}.value`}
                      className="w-full"
                      rightElement={<p>€</p>}
                      tooltip={t("productsParcel.valueTooltip")}
                    />
                    {packagesFieldArray.fields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => packagesFieldArray.remove(index)}
                        className="mt-[15px] flex items-start justify-center hover:bg-gray-100 focus:outline-hidden"
                      >
                        <TrashIcon className="cursor-pointer text-red-600" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                packagesFieldArray.append({
                  value: 0,
                  length: 0,
                  width: 0,
                  height: 0,
                  weight: 0,
                })
              }
              className="bg-brand50 text-brand700 hover:border-brand600 h-fit w-fit rounded-lg px-4 py-3 text-base font-semibold focus:outline-hidden"
            >
              {t("productsParcel.addParcel")}
            </button>
          </div>
        </div>
      </div>
      <div className="shadow-basic flex w-full max-w-[800px] flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex w-full flex-1 flex-row items-center justify-between gap-4">
          <div className="flex flex-1 flex-row items-center justify-between gap-4">
            <ShieldIcon className="text-zinc-900" />
            <div className="flex flex-1 flex-col">
              <p className="font-semibold">{t("productsParcel.insuranceTitle")}</p>
              <p className="hidden md:block">{t("productsParcel.insuranceDescription")}</p>
            </div>
          </div>
          <StyledSwitch name="insurance" control={methods.control} />
        </div>
        <p className="block w-full md:hidden">{t("productsParcel.insuranceDescription")}</p>
      </div>
      <div className="shadow-basic flex w-full max-w-[800px] flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex w-full flex-1 flex-row items-center justify-between gap-4">
          <ShipmentTransitIcon className="text-zinc-900" />
          <div className="flex flex-1 flex-col">
            <p className="font-semibold">{t("productsParcel.expressTitle")}</p>
            <p className="hidden md:block">{t("productsParcel.expressDescription")}</p>
          </div>
          <StyledSwitch name="express" control={methods.control} />
        </div>
        <p className="block md:hidden">{t("productsParcel.expressDescription")}</p>
      </div>
    </div>
  );
};
